import React, { useCallback, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import cm from 'classnames';
import dayjs from 'dayjs';
import { Button } from 'ksnm-common-ui/lib/Button';
import { Drawer } from 'ksnm-common-ui/lib/Drawer';
import { Icon } from 'ksnm-common-ui/lib/Icon';

import { usePermission } from '@src/utils/hooks/usePermission';
import { periodsStore } from '@stores/dictionaryStores/periodsStore';
import { publicationRegisterStore } from '@stores/publicationRegisterStore';
import { addZeroToDate } from '@utils';
import type { ReportingBlockDto, ReportingPackageDto } from '@src/api';

import { Calendar } from './Calendar';
import { Collapse } from './Collapse';
import { additionalPackageInfo, functionality, reportSettings, submissionDocuments } from './constants';
import { PackageInfo } from './PackageInfo';
import { ReportInfo } from './ReportInfo';

import styles from './PublicationRegisterDrawer.module.scss';

interface PublicationRegisterDrawerInterface {
  open: boolean;
  toggle: () => void;
  toggleAddPackageDrawer: () => void;
  selected: ReportingPackageDto & ReportingBlockDto;
}

export const PublicationRegisterDrawerComponent = ({
  open,
  toggle,
  toggleAddPackageDrawer,
  selected,
}: PublicationRegisterDrawerInterface): JSX.Element => {
  const { getPackageInfo, reportPackageInfo, setPackageFormInitials, setIsFormed, setEditPackageMode } =
    publicationRegisterStore;
  const { periods } = periodsStore;

  const cmReportPackagePermission = usePermission('CmReportPackage');

  const {
    reportPackageCode,
    reportCodeExt,
    reportVersionCode,
    reportGroupCode,
    reportPackageYear,
    preparationMonth,
    preparationDay,
    reportPackageCorrection,
    periodCode,
    organization,
  } = selected;

  useEffect(() => {
    if (reportPackageCode && open) {
      getPackageInfo(reportPackageCode, reportCodeExt || undefined, reportVersionCode || undefined);
    }
  }, [getPackageInfo, open, reportCodeExt, reportPackageCode, reportVersionCode]);

  const onEditPackage = useCallback(() => {
    const { reportPackageDescription, reportPackageStatus, reportPackageUserName, buildDate } = reportPackageInfo;

    const disclosureData =
      reportPackageYear && preparationMonth && preparationDay
        ? `${addZeroToDate(preparationDay)}.${addZeroToDate(preparationMonth)}.${reportPackageYear}`
        : '';

    setPackageFormInitials({
      organizationCode: organization,
      year: `${reportPackageYear}`,
      reportGroupCode,
      periodCode,
      correction: reportPackageCorrection,
      description: reportPackageDescription,
      code: reportPackageCode,
      disclosureData,
      creationData: buildDate ? dayjs(new Date(buildDate)).format('DD.MM.YYYY') : '',
      status: reportPackageStatus,
      username: reportPackageUserName,
    });
    toggle();
    setEditPackageMode(true);
    setIsFormed(true);
    toggleAddPackageDrawer();
  }, [
    organization,
    periodCode,
    preparationDay,
    preparationMonth,
    reportGroupCode,
    reportPackageCode,
    reportPackageCorrection,
    reportPackageInfo,
    reportPackageYear,
    setEditPackageMode,
    setIsFormed,
    setPackageFormInitials,
    toggle,
    toggleAddPackageDrawer,
  ]);

  const footer = useMemo(
    () => (
      <div className={styles.btn}>
        <Button
          icon={<Icon name="edit" />}
          disabled={!!reportCodeExt || !cmReportPackagePermission.UPDATE}
          onClick={onEditPackage}
          size="large"
        >
          Редактировать
        </Button>

        <Button icon={<Icon name="done" />} disabled size="large">
          Сохранить
        </Button>
      </div>
    ),
    [cmReportPackagePermission.UPDATE, onEditPackage, reportCodeExt],
  );

  return (
    <Drawer open={open} onClose={toggle} title={reportCodeExt ? reportCodeExt : reportPackageCode} footer={footer}>
      <>
        <PackageInfo />
        {reportCodeExt && <ReportInfo />}
        <Calendar />

        {reportCodeExt && (
          <>
            <Collapse
              title="Порядок представления документов (информации)"
              className={styles.collapse}
              closeItem={
                <div className={styles.icons}>
                  {submissionDocuments.map((item) => (
                    <div key={item.value} title={item.title}>
                      <Icon name={item.iconName} fill={reportPackageInfo[item.value] === '1' ? '#58BF7F' : '#F26050'} />
                    </div>
                  ))}
                </div>
              }
              openItems={
                <>
                  {submissionDocuments.map((item) => {
                    let fill = reportPackageInfo[item.value] === '1' ? '#58BF7F' : '#F26050';
                    if (item.value === 'formTCC') {
                      fill = '#9EB3C8';
                    }
                    return (
                      <div className={cm(styles.collapseItem)} key={item.value}>
                        <Icon name={item.iconName} fill={fill} />
                        {item.value === 'formTCC' ? (
                          <p>
                            {item.title}: <span>{reportPackageInfo[item.value]}</span>
                          </p>
                        ) : (
                          <p>{item.title}</p>
                        )}
                      </div>
                    );
                  })}
                </>
              }
            />

            <Collapse
              title="Функциональные возможности информационных систем организации по работе с документами (информацией)"
              className={styles.collapse}
              closeItem={
                <div className={styles.icons}>
                  {functionality.map((item) => (
                    <div key={item.value} title={item.title}>
                      <Icon name={item.iconName} fill={reportPackageInfo[item.value] === '1' ? '#58BF7F' : '#F26050'} />
                    </div>
                  ))}
                </div>
              }
              openItems={
                <>
                  {functionality.map((item) => (
                    <div className={cm(styles.collapseItem)} key={item.value}>
                      <Icon name={item.iconName} fill={reportPackageInfo[item.value] === '1' ? '#58BF7F' : '#F26050'} />
                      <p>{item.title}</p>
                    </div>
                  ))}
                </>
              }
            />

            <Collapse
              className={styles.collapse}
              title="Технические настройки отчета"
              closeItem=""
              openItems={
                <>
                  {reportSettings
                    .filter((item) => reportPackageInfo[item.value] !== null)
                    .map((item) => {
                      let value = reportPackageInfo[item.value];

                      if (item.value === 'reportSettingsNewWindow') {
                        value = reportPackageInfo[item.value] ? 'Да' : 'Нет';
                      }

                      return (
                        <div className={cm(styles.collapseItem)} key={item.value}>
                          <p>
                            {item.title}: <span>{value}</span>
                          </p>
                        </div>
                      );
                    })}
                </>
              }
            />
          </>
        )}

        <Collapse
          title="Дополнительная информация о пакете"
          className={styles.collapse}
          closeItem=""
          openItems={
            <>
              {additionalPackageInfo
                .filter((item) => reportPackageInfo[item.value] !== null)
                .map((item) => {
                  let value = reportPackageInfo[item.value];

                  if (item.value === 'periodCode') {
                    const period = periods.find((p) => p.code === reportPackageInfo[item.value]);
                    value = period ? `${period.code} - ${period.name}` : '';
                  }

                  return (
                    <div className={cm(styles.collapseItem)} key={item.value}>
                      <p>
                        {item.title}: <span>{value}</span>
                      </p>
                    </div>
                  );
                })}
            </>
          }
        />
      </>
    </Drawer>
  );
};

export const PublicationRegisterDrawer = observer(PublicationRegisterDrawerComponent);
